import React, { useMemo } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { normalizeTimeToHHmm, timeToMinutes } from '@/utils/availability';

interface TimeInput24hProps {
  value: string; // HH:mm format
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
  minuteStep?: number; // Step for minutes (default: 10, use 1 for every minute, 5 for every 5 minutes)
  /** When set, only times strictly after this HH:mm are selectable (for end times). */
  minTime?: string;
}

const TimeInput24h: React.FC<TimeInput24hProps> = ({
  value,
  onChange,
  disabled = false,
  className,
  minuteStep = 10,
  minTime,
}) => {
  const parseTime = (timeStr: string): [number, number] => {
    const normalized = normalizeTimeToHHmm(timeStr);
    const [h, m] = normalized.split(':').map(Number);
    return [
      Math.max(0, Math.min(23, h ?? 0)),
      Math.max(0, Math.min(59, m ?? 0)),
    ];
  };

  const [hours, minutes] = parseTime(value);
  const minMinutesExclusive = minTime != null ? timeToMinutes(minTime) : null;

  const hoursArray = useMemo(
    () =>
      Array.from({ length: 24 }, (_, i) => ({
        value: i.toString().padStart(2, '0'),
        label: i.toString().padStart(2, '0'),
      })),
    []
  );

  const minutesArray = useMemo(
    () =>
      Array.from({ length: Math.floor(60 / minuteStep) }, (_, i) => {
        const minute = i * minuteStep;
        return {
          value: minute.toString().padStart(2, '0'),
          label: minute.toString().padStart(2, '0'),
        };
      }),
    [minuteStep]
  );

  const isTimeValid = (hour: number, minute: number) =>
    minMinutesExclusive === null || hour * 60 + minute > minMinutesExclusive;

  const validHours = useMemo(
    () =>
      hoursArray.filter(h =>
        minutesArray.some(m =>
          isTimeValid(parseInt(h.value, 10), parseInt(m.value, 10))
        )
      ),
    [hoursArray, minutesArray, minMinutesExclusive]
  );

  const validMinutesForHour = (hour: number) =>
    minutesArray.filter(m => isTimeValid(hour, parseInt(m.value, 10)));

  const getClosestMinute = (currentMinute: number): number => {
    const rounded = Math.round(currentMinute / minuteStep) * minuteStep;
    return rounded >= 60 ? 60 - minuteStep : rounded;
  };

  const validMinutes = validMinutesForHour(hours);
  const minuteSelectValue =
    validMinutes.find(m => m.value === minutes.toString().padStart(2, '0'))
      ?.value ??
    validMinutes[0]?.value ??
    getClosestMinute(minutes).toString().padStart(2, '0');

  const hourSelectValue = validHours.some(
    h => h.value === hours.toString().padStart(2, '0')
  )
    ? hours.toString().padStart(2, '0')
    : (validHours[0]?.value ?? '00');

  const handleHourChange = (newHour: string) => {
    const hour = parseInt(newHour, 10);
    const allowedMinutes = validMinutesForHour(hour);
    const currentMinute = parseInt(minuteSelectValue, 10);
    const minute =
      allowedMinutes.find(m => parseInt(m.value, 10) === currentMinute) ??
      allowedMinutes[0];
    const minuteNum = minute ? parseInt(minute.value, 10) : 0;
    onChange(
      `${hour.toString().padStart(2, '0')}:${minuteNum.toString().padStart(2, '0')}`
    );
  };

  const handleMinuteChangeWithSync = (newMinute: string) => {
    const minute = parseInt(newMinute, 10);
    onChange(
      `${hours.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
    );
  };

  const noValidTimes = validHours.length === 0;

  return (
    <div className={cn('flex items-center gap-1', className)}>
      <Select
        value={hourSelectValue}
        onValueChange={handleHourChange}
        disabled={disabled || noValidTimes}
      >
        <SelectTrigger className="h-9 w-16">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {validHours.map(hour => (
            <SelectItem key={hour.value} value={hour.value}>
              {hour.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <span className="font-medium text-muted-foreground">:</span>
      <Select
        value={minuteSelectValue}
        onValueChange={handleMinuteChangeWithSync}
        disabled={disabled || noValidTimes || validMinutes.length === 0}
      >
        <SelectTrigger className="h-9 w-16">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {validMinutes.map(minute => (
            <SelectItem key={minute.value} value={minute.value}>
              {minute.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export default TimeInput24h;
